import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import {
  Package,
  AlertTriangle,
  CheckCircle,
  Clock,
  MapPin,
  Phone,
  Mail,
  FileText,
  Image as ImageIcon,
  Calendar,
  Truck,
  Save,
  ArrowLeft,
  ExternalLink,
  MessageSquare,
  Bell,
  X,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';

interface PartRequest {
  id: string;
  created_at: string;
  brand: string;
  job_id: string | null;
  customer_first: string;
  customer_last: string;
  customer_phone: string;
  customer_email: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  geo_lat: number | null;
  geo_lng: number | null;
  equipment_type: string;
  ah_model: string;
  ah_serial: string;
  co_model: string;
  co_serial: string;
  furnace_model: string;
  furnace_serial: string;
  description: string;
  rust_present: boolean;
  condition_notes: string;
  suspected_part_name: string;
  suspected_part_number: string;
  alt_part_numbers: string[];
  ai_summary: string;
  status: string;
  eta_date: string | null;
  vendor_name: string | null;
  po_number: string | null;
  tracking_number: string | null;
  pdf_url: string | null;
  escalate_level: number;
  last_update_at: string;
}

interface Photo {
  id: string;
  created_at: string;
  category: string;
  url: string;
  ai_text: string | null;
  ai_fields: any;
}

interface Event {
  id: string;
  created_at: string;
  event_type: string;
  payload: any;
}

interface Watcher {
  id: string;
  kind: string;
  value: string;
  active: boolean;
}

export default function PartsRequestDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<PartRequest | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [watchers, setWatchers] = useState<Watcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Edit states
  const [editMode, setEditMode] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [formData, setFormData] = useState({
    status: '',
    vendor_name: '',
    po_number: '',
    tracking_number: '',
    eta_date: '',
  });

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  async function loadData() {
    try {
      setLoading(true);

      // Load request
      const { data: reqData, error: reqError } = await supabase
        .from('part_requests')
        .select('*')
        .eq('id', id)
        .single();

      if (reqError) throw reqError;
      setRequest(reqData);
      setFormData({
        status: reqData.status || '',
        vendor_name: reqData.vendor_name || '',
        po_number: reqData.po_number || '',
        tracking_number: reqData.tracking_number || '',
        eta_date: reqData.eta_date || '',
      });

      // Load photos
      const { data: photoData, error: photoError } = await supabase
        .from('part_request_photos')
        .select('*')
        .eq('request_id', id)
        .order('created_at', { ascending: true });

      if (photoError) throw photoError;
      setPhotos(photoData || []);

      // Load events
      const { data: eventData, error: eventError } = await supabase
        .from('part_request_events')
        .select('*')
        .eq('request_id', id)
        .order('created_at', { ascending: false });

      if (eventError) throw eventError;
      setEvents(eventData || []);

      // Load watchers
      const { data: watcherData, error: watcherError } = await supabase
        .from('part_watchers')
        .select('*')
        .eq('request_id', id);

      if (watcherError) throw watcherError;
      setWatchers(watcherData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function handleUpdate() {
    if (!request) return;

    try {
      setSaving(true);

      const { error } = await supabase
        .from('part_requests')
        .update({
          status: formData.status,
          vendor_name: formData.vendor_name || null,
          po_number: formData.po_number || null,
          tracking_number: formData.tracking_number || null,
          eta_date: formData.eta_date || null,
          last_update_at: new Date().toISOString(),
        })
        .eq('id', request.id);

      if (error) throw error;

      // Log event
      await supabase.from('part_request_events').insert({
        request_id: request.id,
        event_type: 'status',
        payload: {
          status: formData.status,
          vendor_name: formData.vendor_name,
          po_number: formData.po_number,
          tracking_number: formData.tracking_number,
          eta_date: formData.eta_date,
        },
      });

      setEditMode(false);
      await loadData();
    } catch (error) {
      console.error('Error updating request:', error);
      alert('Failed to update request');
    } finally {
      setSaving(false);
    }
  }

  async function handleAddNote() {
    if (!request || !newNote.trim()) return;

    try {
      setSaving(true);

      await supabase.from('part_request_events').insert({
        request_id: request.id,
        event_type: 'note',
        payload: { note: newNote },
      });

      setNewNote('');
      await loadData();
    } catch (error) {
      console.error('Error adding note:', error);
      alert('Failed to add note');
    } finally {
      setSaving(false);
    }
  }

  function openPhotoLightbox(photo: Photo, index: number) {
    setSelectedPhoto(photo);
    setCurrentPhotoIndex(index);
  }

  function closePhotoLightbox() {
    setSelectedPhoto(null);
  }

  function navigatePhoto(direction: 'prev' | 'next') {
    const newIndex = direction === 'prev'
      ? (currentPhotoIndex - 1 + photos.length) % photos.length
      : (currentPhotoIndex + 1) % photos.length;
    
    setCurrentPhotoIndex(newIndex);
    setSelectedPhoto(photos[newIndex]);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Request Not Found</h2>
        <p className="text-gray-600 mb-6">The part request you're looking for doesn't exist.</p>
        <Link
          to="/parts"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Requests
        </Link>
      </div>
    );
  }

  const shortId = request.id.split('-')[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/parts')}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Part Request #{shortId}
            </h1>
            <p className="text-gray-600 mt-1">
              {request.customer_first} {request.customer_last}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {request.escalate_level > 0 && (
            <span className="inline-flex items-center gap-2 px-3 py-2 bg-red-100 text-red-800 rounded-lg font-medium">
              <AlertTriangle className="w-4 h-4" />
              Escalation Level {request.escalate_level}
            </span>
          )}
          <span className={`px-3 py-2 rounded-lg font-medium ${
            request.status === 'new' ? 'bg-blue-100 text-blue-800' :
            request.status === 'ordered' ? 'bg-yellow-100 text-yellow-800' :
            request.status === 'received' ? 'bg-green-100 text-green-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {request.status}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Customer & Location */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Customer & Location</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Name</p>
                <p className="font-medium">{request.customer_first} {request.customer_last}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Phone</p>
                <a href={`tel:${request.customer_phone}`} className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                  <Phone className="w-4 h-4" />
                  {request.customer_phone}
                </a>
              </div>
              {request.customer_email && (
                <div className="col-span-2">
                  <p className="text-sm text-gray-600">Email</p>
                  <a href={`mailto:${request.customer_email}`} className="font-medium text-blue-600 hover:underline flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {request.customer_email}
                  </a>
                </div>
              )}
              <div className="col-span-2">
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-medium">
                  {request.address1}
                  {request.address2 && <>, {request.address2}</>}
                  <br />
                  {request.city}, {request.state} {request.zip}
                </p>
                {request.geo_lat && request.geo_lng && (
                  <a
                    href={`https://www.google.com/maps?q=${request.geo_lat},${request.geo_lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1"
                  >
                    <MapPin className="w-3 h-3" />
                    View on Map
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Equipment Details */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Equipment Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <p className="text-sm text-gray-600">System Type</p>
                <p className="font-medium capitalize">{request.equipment_type}</p>
              </div>
              {request.ah_model && (
                <div>
                  <p className="text-sm text-gray-600">Air Handler Model</p>
                  <p className="font-medium">{request.ah_model}</p>
                </div>
              )}
              {request.ah_serial && (
                <div>
                  <p className="text-sm text-gray-600">Air Handler Serial</p>
                  <p className="font-medium">{request.ah_serial}</p>
                </div>
              )}
              {request.co_model && (
                <div>
                  <p className="text-sm text-gray-600">Condenser Model</p>
                  <p className="font-medium">{request.co_model}</p>
                </div>
              )}
              {request.co_serial && (
                <div>
                  <p className="text-sm text-gray-600">Condenser Serial</p>
                  <p className="font-medium">{request.co_serial}</p>
                </div>
              )}
              {request.furnace_model && (
                <div>
                  <p className="text-sm text-gray-600">Furnace Model</p>
                  <p className="font-medium">{request.furnace_model}</p>
                </div>
              )}
              {request.furnace_serial && (
                <div>
                  <p className="text-sm text-gray-600">Furnace Serial</p>
                  <p className="font-medium">{request.furnace_serial}</p>
                </div>
              )}
            </div>
          </div>

          {/* Problem Description */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Problem Description</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-1">Issue Description</p>
                <p className="text-gray-900">{request.description}</p>
              </div>
              {request.condition_notes && (
                <div>
                  <p className="text-sm text-gray-600 mb-1">Condition Notes</p>
                  <p className="text-gray-900">{request.condition_notes}</p>
                </div>
              )}
              {request.ai_summary && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-medium mb-1">AI Summary</p>
                  <p className="text-blue-900">{request.ai_summary}</p>
                </div>
              )}
              <div className="flex items-center gap-4 text-sm">
                <span className="text-gray-600">Rust Present:</span>
                <span className={`font-medium ${request.rust_present ? 'text-red-600' : 'text-green-600'}`}>
                  {request.rust_present ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>

          {/* Part Information */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Part Information</h2>
            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-600">Part Name</p>
                <p className="font-medium">{request.suspected_part_name || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Part Number</p>
                <p className="font-medium">{request.suspected_part_number || 'Not specified'}</p>
              </div>
              {request.alt_part_numbers && request.alt_part_numbers.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600">Alternate Part Numbers</p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {request.alt_part_numbers.map((alt, i) => (
                      <span key={i} className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-sm">
                        {alt}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Photos */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <ImageIcon className="w-5 h-5" />
              Photos ({photos.length})
            </h2>
            {photos.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No photos uploaded</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {photos.map((photo, index) => (
                  <div key={photo.id} className="group relative">
                    <button
                      onClick={() => openPhotoLightbox(photo, index)}
                      className="relative aspect-square w-full rounded-lg overflow-hidden bg-gray-100 hover:opacity-90 transition-opacity"
                    >
                      <img
                        src={photo.url}
                        alt={photo.category}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                    <p className="text-xs text-gray-600 mt-1 truncate">{photo.category}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Events Timeline */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Activity Timeline</h2>
            {events.length === 0 ? (
              <p className="text-gray-600 text-center py-8">No activity yet</p>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <div key={event.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                      {event.event_type === 'created' ? <CheckCircle className="w-4 h-4 text-blue-600" /> :
                       event.event_type === 'status' ? <Clock className="w-4 h-4 text-blue-600" /> :
                       event.event_type === 'note' ? <MessageSquare className="w-4 h-4 text-blue-600" /> :
                       <FileText className="w-4 h-4 text-blue-600" />}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 capitalize">{event.event_type}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(event.created_at).toLocaleString()}
                      </p>
                      {event.payload && (
                        <div className="mt-2 text-sm text-gray-700 bg-gray-50 rounded p-2">
                          {event.payload.note && <p>{event.payload.note}</p>}
                          {event.payload.status && <p>Status: {event.payload.status}</p>}
                          {event.payload.vendor_name && <p>Vendor: {event.payload.vendor_name}</p>}
                          {event.payload.po_number && <p>PO: {event.payload.po_number}</p>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Action Panel */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions</h2>
            
            {!editMode ? (
              <div className="space-y-4">
                <button
                  onClick={() => setEditMode(true)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Update Status
                </button>

                {request.pdf_url && (
                  <a
                    href={request.pdf_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2"
                  >
                    <FileText className="w-4 h-4" />
                    View PDF
                  </a>
                )}

                {request.job_id && (
                  <div className="pt-4 border-t">
                    <p className="text-sm text-gray-600">SuccessWare Job</p>
                    <p className="font-medium">{request.job_id}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="new">New</option>
                    <option value="ordered">Ordered</option>
                    <option value="received">Received</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vendor Name
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., Ferguson HVAC"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PO Number
                  </label>
                  <input
                    type="text"
                    value={formData.po_number}
                    onChange={(e) => setFormData({ ...formData, po_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Purchase order #"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tracking Number
                  </label>
                  <input
                    type="text"
                    value={formData.tracking_number}
                    onChange={(e) => setFormData({ ...formData, tracking_number: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="UPS/FedEx tracking #"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ETA Date
                  </label>
                  <input
                    type="date"
                    value={formData.eta_date}
                    onChange={(e) => setFormData({ ...formData, eta_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleUpdate}
                    disabled={saving}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    <Save className="w-4 h-4" />
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    disabled={saving}
                    className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Add Note */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Add Note</h2>
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note or comment..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-3"
            />
            <button
              onClick={handleAddNote}
              disabled={saving || !newNote.trim()}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Add Note
            </button>
          </div>

          {/* Metadata */}
          <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Request Info</h2>
            <div className="space-y-3 text-sm">
              <div>
                <p className="text-gray-600">Request ID</p>
                <p className="font-mono text-xs">{request.id}</p>
              </div>
              <div>
                <p className="text-gray-600">Brand</p>
                <p className="font-medium">{request.brand}</p>
              </div>
              <div>
                <p className="text-gray-600">Created</p>
                <p>{new Date(request.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-600">Last Updated</p>
                <p>{new Date(request.last_update_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Photo Lightbox */}
      {selectedPhoto && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-4">
          <button
            onClick={closePhotoLightbox}
            className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>

          <button
            onClick={() => navigatePhoto('prev')}
            className="absolute left-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          <button
            onClick={() => navigatePhoto('next')}
            className="absolute right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          <div className="max-w-4xl max-h-full">
            <img
              src={selectedPhoto.url}
              alt={selectedPhoto.category}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
            />
            <div className="mt-4 text-white text-center">
              <p className="font-medium capitalize">{selectedPhoto.category}</p>
              <p className="text-sm text-gray-300 mt-1">
                Photo {currentPhotoIndex + 1} of {photos.length}
              </p>
              {selectedPhoto.ai_text && (
                <details className="mt-4 text-left bg-white bg-opacity-10 rounded-lg p-4">
                  <summary className="cursor-pointer font-medium">AI Extracted Text</summary>
                  <pre className="mt-2 text-xs whitespace-pre-wrap">{selectedPhoto.ai_text}</pre>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
